import React, { useEffect, useState } from "react";
import { X, Copy, Check, Utensils } from "lucide-react";
import { RESTAURANT } from "@/data/restaurant";

const STORAGE_KEY = "infuse-welcome-offer-dismissed-v2";
const PROMO_CODE = "WECOME10";
const OFFER_END = new Date("2026-11-10T23:59:59Z");

export default function WelcomeOffer() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (Date.now() > OFFER_END.getTime()) return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch (_) { /* storage disabled */ }
    const t = setTimeout(() => setOpen(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) {}
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* clipboard unavailable */ }
  };

  if (!open) return null;

  return (
    <div
      data-testid="welcome-offer-modal"
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-offer-title"
    >
      {/* backdrop */}
      <button
        aria-label="Close offer"
        onClick={close}
        className="absolute inset-0 bg-[rgba(6,30,25,0.72)] backdrop-blur-sm"
        data-testid="welcome-offer-backdrop"
      />

      <div className="relative w-full max-w-md bg-[var(--infuse-cream)] border border-[var(--infuse-gold)]/40 shadow-2xl">
        {/* decorative emerald top band */}
        <div className="relative h-32 bg-[var(--infuse-emerald)] overflow-hidden">
          <div className="absolute inset-0 opacity-25" style={{
            backgroundImage: "radial-gradient(circle at 20% 30%, rgba(212,175,55,0.5) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(197,48,48,0.35) 0%, transparent 55%)",
          }} />
          <div className="relative h-full flex items-center justify-center">
            <span className="font-eyebrow text-[0.72rem] text-[var(--infuse-gold-soft)] tracking-[0.42em]">
              ✦ Welcome Offer ✦
            </span>
          </div>
        </div>

        <button
          onClick={close}
          data-testid="welcome-offer-close"
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 transition-colors"
        >
          <X size={16} strokeWidth={1.5} />
        </button>

        <div className="p-8 md:p-10 text-center">
          <h2
            id="welcome-offer-title"
            className="font-display text-4xl md:text-[2.6rem] text-[var(--infuse-emerald)] leading-[1.05]"
          >
            Enjoy <em className="not-italic text-[var(--infuse-pomegranate)]">10% off</em> when you order online.
          </h2>

          <p className="mt-4 text-[var(--infuse-ink)]/70 leading-relaxed">
            A little gift from our kitchen to your door. Use this code at checkout.
          </p>

          {/* code chip */}
          <div className="mt-7 inline-flex items-stretch border border-dashed border-[var(--infuse-gold)]/80 bg-white">
            <div className="px-6 py-4 font-display text-3xl tracking-[0.3em] text-[var(--infuse-emerald)]">
              {PROMO_CODE}
            </div>
            <button
              onClick={copyCode}
              data-testid="welcome-offer-copy"
              aria-label="Copy code"
              className="px-5 border-l border-[var(--infuse-gold)]/40 bg-[var(--infuse-emerald)] text-white hover:bg-[var(--infuse-emerald-dark)] transition-colors flex items-center gap-2 text-sm font-eyebrow"
            >
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
            </button>
          </div>

          <a
            href={RESTAURANT.orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="welcome-offer-order-cta"
            onClick={close}
            className="btn-primary mt-8 w-full"
          >
            Order Online <Utensils size={14} strokeWidth={1.5} />
          </a>

          <button
            onClick={close}
            data-testid="welcome-offer-maybe-later"
            className="mt-3 font-eyebrow text-[0.68rem] text-[var(--infuse-ink)]/50 hover:text-[var(--infuse-pomegranate)] transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
